/* ==========================================================================
   VELVET VEILS — Collection filtering, sorting and view switching
   Filters use Shopify's native storefront filtering. Every change rewrites the
   URL, fetches the same URL, and swaps in the freshly rendered results — so
   deep links, refreshes and browser back/forward all behave correctly.
   ========================================================================== */

(function () {
  'use strict';

  var L = (window.Lakshiv = window.Lakshiv || {});
  var utils = L.utils;
  if (!utils) return;

  var SWAP_TARGETS = [
    '[data-collection-results]',
    '[data-collection-filters]',
    '[data-collection-count]',
    '[data-active-filters]',
    '[data-mobile-filters]'
  ];

  var Collection = {
    section: null,
    controller: null,

    init: function () {
      this.section = utils.qs('[data-collection-section]');
      if (!this.section) return;

      this.bind();
      this.restoreView();
    },

    bind: function () {
      var self = this;

      // Any filter form change (checkboxes, price inputs, availability).
      this.section.addEventListener('change', function (event) {
        var form = event.target.closest('[data-filter-form]');
        if (!form) return;
        // Price inputs get their own debounced handler below.
        if (event.target.matches('[data-price-input], input[type="range"]')) return;
        self.applyForm(form);
      });

      this.section.addEventListener(
        'input',
        utils.debounce(function (event) {
          if (!event.target.matches('[data-price-input]')) return;
          var form = event.target.closest('[data-filter-form]');
          if (form) self.applyForm(form);
        }, 600)
      );

      // Sort dropdown.
      this.section.addEventListener('change', function (event) {
        var select = event.target.closest('[data-sort-by]');
        if (!select) return;
        var url = new URL(window.location.href);
        url.searchParams.set('sort_by', select.value);
        url.searchParams.delete('page');
        self.navigate(url.toString());
      });

      // Links that already carry filter params (chips, "remove filter", clear all).
      this.section.addEventListener('click', function (event) {
        var link = event.target.closest('[data-filter-link]');
        if (link) {
          event.preventDefault();
          self.navigate(link.href);
          return;
        }

        var page = event.target.closest('[data-pagination] a');
        if (page) {
          event.preventDefault();
          self.navigate(page.href, { scroll: true });
          return;
        }

        var view = event.target.closest('[data-view-toggle]');
        if (view) {
          event.preventDefault();
          self.setView(view.getAttribute('data-view-toggle'));
        }
      });

      // Dual-handle price slider keeps the two thumbs from crossing.
      utils.qsa('[data-price-range]', this.section).forEach(function (wrapper) {
        self.initPriceRange(wrapper);
      });

      window.addEventListener('popstate', function () {
        self.render(window.location.href, { push: false });
      });
    },

    /** Serialises a filter form into a URL and navigates to it. */
    applyForm: function (form) {
      var params = new URLSearchParams(new FormData(form));

      // Empty price bounds should not appear in the URL at all.
      ['filter.v.price.gte', 'filter.v.price.lte'].forEach(function (key) {
        if (params.get(key) === '' || params.get(key) === null) params.delete(key);
      });

      var url = new URL(window.location.href);
      var preservedSort = url.searchParams.get('sort_by');
      url.search = params.toString();
      if (preservedSort && !params.get('sort_by')) {
        url.searchParams.set('sort_by', preservedSort);
      }
      url.searchParams.delete('page');

      this.navigate(url.toString());
    },

    navigate: function (url, opts) {
      this.render(url, Object.assign({ push: true }, opts || {}));
    },

    render: function (url, opts) {
      var self = this;
      opts = opts || {};

      var results = utils.qs('[data-collection-results]');
      if (results) results.classList.add('is-loading');

      if (this.controller) this.controller.abort();
      this.controller = new AbortController();

      fetch(url, { signal: this.controller.signal })
        .then(function (res) {
          if (!res.ok) throw new Error('Filter request failed');
          return res.text();
        })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');

          SWAP_TARGETS.forEach(function (selector) {
            var fresh = doc.querySelector(selector);
            var live = utils.qs(selector);
            if (fresh && live) live.innerHTML = fresh.innerHTML;
          });

          if (opts.push !== false) {
            window.history.pushState({ filtered: true }, '', url);
          }

          if (opts.scroll) {
            var top = utils.qs('[data-collection-results]');
            if (top) {
              window.scrollTo({
                top: top.getBoundingClientRect().top + window.scrollY - 120,
                behavior: utils.prefersReducedMotion() ? 'auto' : 'smooth'
              });
            }
          }

          self.restoreView();
          document.dispatchEvent(new CustomEvent('lakshiv:section:loaded'));
          if (L.observeReveal) L.observeReveal();
        })
        .catch(function (error) {
          if (error.name === 'AbortError') return;
          // Fall back to a normal page load rather than leaving a dead UI.
          window.location.href = url;
        })
        .finally(function () {
          var el = utils.qs('[data-collection-results]');
          if (el) el.classList.remove('is-loading');
        });
    },

    /* --- Grid / list view -------------------------------------------------- */

    setView: function (view) {
      try {
        window.localStorage.setItem('lakshiv:collection-view', view);
      } catch (e) {
        /* storage unavailable — the choice just won't persist */
      }
      this.applyView(view);
    },

    restoreView: function () {
      var stored;
      try {
        stored = window.localStorage.getItem('lakshiv:collection-view');
      } catch (e) {
        stored = null;
      }
      this.applyView(stored || 'grid');
    },

    applyView: function (view) {
      var grid = utils.qs('[data-product-grid]');
      if (grid) grid.classList.toggle('grid--list', view === 'list');

      utils.qsa('[data-view-toggle]').forEach(function (btn) {
        btn.setAttribute(
          'aria-pressed',
          String(btn.getAttribute('data-view-toggle') === view)
        );
      });
    },

    /* --- Price range slider ------------------------------------------------ */

    initPriceRange: function (wrapper) {
      var minRange = wrapper.querySelector('[data-range-min]');
      var maxRange = wrapper.querySelector('[data-range-max]');
      var minInput = wrapper.querySelector('[data-price-min]');
      var maxInput = wrapper.querySelector('[data-price-max]');
      var fill = wrapper.querySelector('.price-range__fill');
      if (!minRange || !maxRange) return;

      function paint() {
        var min = Number(minRange.min);
        var max = Number(minRange.max);
        var span = max - min || 1;
        var lo = ((Number(minRange.value) - min) / span) * 100;
        var hi = ((Number(maxRange.value) - min) / span) * 100;
        if (fill) {
          fill.style.left = lo + '%';
          fill.style.width = Math.max(0, hi - lo) + '%';
        }
      }

      function clamp(changed) {
        var lo = Number(minRange.value);
        var hi = Number(maxRange.value);
        if (lo > hi) {
          if (changed === 'min') {
            minRange.value = hi;
          } else {
            maxRange.value = lo;
          }
        }
        if (minInput) minInput.value = minRange.value;
        if (maxInput) maxInput.value = maxRange.value;
        paint();
      }

      utils.on(minRange, 'input', function () {
        clamp('min');
      });
      utils.on(maxRange, 'input', function () {
        clamp('max');
      });

      // Typing into the number fields drives the slider back.
      utils.on(minInput, 'change', function () {
        minRange.value = minInput.value || minRange.min;
        clamp('min');
      });
      utils.on(maxInput, 'change', function () {
        maxRange.value = maxInput.value || maxRange.max;
        clamp('max');
      });

      paint();
    }
  };

  L.collection = Collection;

  function init() {
    Collection.init();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);
})();
